/**
 * Push-to-talk error catalog: every code has a technical description (logs), a user-facing message
 * (short: the driver is driving) and the recovery strategy clients apply. No zod here so web bundles
 * that only need messages do not pull the schema library.
 */
import type { FloorEndReason, RadioErrorCode } from '@despachador/domain';

export type PttRecovery =
  /** Nothing to do automatically; the user can simply try again. */
  | 'RETRY'
  /** The client reconnects on its own (backoff). */
  | 'AUTO_RECONNECT'
  /** The user must act (browser permission, plug a microphone...). */
  | 'USER_ACTION'
  /** Wait until the restriction/limit ends. */
  | 'WAIT'
  /** Go back to the channel list. */
  | 'PICK_CHANNEL'
  /** Session gone: sign in again. */
  | 'SIGN_IN';

export interface PttErrorInfo {
  technical: string;
  user: string;
  recovery: PttRecovery;
}

export const PTT_ERRORS: Record<RadioErrorCode, PttErrorInfo> = {
  MICROPHONE_PERMISSION_DENIED: {
    technical: 'getUserMedia rejected (NotAllowedError/SecurityError)',
    user: 'Micrófono bloqueado. Permite el micrófono para este sitio (icono del candado) e inténtalo de nuevo.',
    recovery: 'USER_ACTION',
  },
  MICROPHONE_UNAVAILABLE: {
    technical: 'getUserMedia failed (NotFoundError/NotReadableError/OverconstrainedError) or track ended',
    user: 'No hay micrófono disponible o lo está usando otra aplicación.',
    recovery: 'USER_ACTION',
  },
  CHANNEL_NOT_FOUND: { technical: 'channel missing, deleted or not visible under RLS', user: 'El canal ya no existe.', recovery: 'PICK_CHANNEL' },
  CHANNEL_ACCESS_DENIED: { technical: 'subject not in channel audience or lacks communication.view/listen', user: 'No tienes acceso a este canal.', recovery: 'PICK_CHANNEL' },
  TRANSMISSION_DENIED: { technical: 'lacks communication.transmit or LISTENER role', user: 'En este canal solo puedes escuchar.', recovery: 'PICK_CHANNEL' },
  CHANNEL_BUSY: { technical: 'floor held by another user', user: 'Canal ocupado. Espera a que termine.', recovery: 'RETRY' },
  CONNECTION_FAILED: { technical: 'request timed out or transport creation failed', user: 'No se pudo conectar. Inténtalo de nuevo.', recovery: 'RETRY' },
  REALTIME_DISCONNECTED: { technical: 'WebSocket closed / heartbeat lost', user: 'Sin conexión. Reconectando…', recovery: 'AUTO_RECONNECT' },
  WEBRTC_FAILED: { technical: 'ICE/DTLS failed after ICE restart', user: 'Falló el audio. Reconectando…', recovery: 'AUTO_RECONNECT' },
  TURN_UNAVAILABLE: { technical: 'no relay candidate and direct path blocked', user: 'Tu red bloquea el audio. Prueba con datos móviles u otra red WiFi.', recovery: 'USER_ACTION' },
  SESSION_EXPIRED: { technical: 'session revoked/expired or user inactive', user: 'Tu sesión terminó. Inicia sesión de nuevo.', recovery: 'SIGN_IN' },
  USER_BLOCKED: { technical: 'active BLOCK restriction', user: 'Un moderador bloqueó tu acceso a la radio.', recovery: 'WAIT' },
  USER_MUTED: { technical: 'active MUTE restriction', user: 'Un moderador te silenció. Puedes escuchar.', recovery: 'WAIT' },
  RATE_LIMITED: { technical: 'per-user/connection rate limit exceeded', user: 'Demasiados intentos. Espera unos segundos.', recovery: 'WAIT' },
  FEATURE_DISABLED: { technical: "tenant feature 'ptt' disabled", user: 'La radio no está habilitada para tu empresa.', recovery: 'SIGN_IN' },
  NO_CHANNEL: { technical: 'no active channel selected', user: 'Elige un canal.', recovery: 'PICK_CHANNEL' },
  TRANSMISSION_ENDED: { technical: 'turn ended by the server', user: 'Tu transmisión terminó.', recovery: 'RETRY' },
};

/** Why a turn ended, as told to the speaker. */
export const PTT_END_REASON_TEXT: Record<FloorEndReason, string> = {
  RELEASED: 'Transmisión finalizada',
  MAX_DURATION: 'Tiempo máximo de transmisión alcanzado',
  LEASE_EXPIRED: 'Se perdió la conexión mientras hablabas',
  NO_AUDIO: 'No llegó audio de tu micrófono',
  DISCONNECTED: 'Conexión perdida',
  FORCE_ENDED: 'Un moderador finalizó tu transmisión',
  PERMISSION_LOST: 'Ya no tienes permiso para transmitir',
  LEFT_CHANNEL: 'Saliste del canal',
};

export function pttErrorText(code: RadioErrorCode | string | undefined, detail?: string): string {
  if (code === 'CHANNEL_BUSY' && detail) return `Canal ocupado: ${detail} está hablando.`;
  if (code === 'TRANSMISSION_ENDED' && detail && detail in PTT_END_REASON_TEXT) return PTT_END_REASON_TEXT[detail as FloorEndReason];
  return (code && PTT_ERRORS[code as RadioErrorCode]?.user) || 'Error de radio.';
}
